import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, 
  Clock, 
  BookOpen, 
  Home, 
  TrendingUp, 
  Save, 
  Building2, 
  Calendar, 
  FileText, 
  Send, 
  Eye,
  DollarSign,
  Award,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ImpactDashboard() {
  const [metrics, setMetrics] = useState({
    total_volunteers: 0,
    active_volunteers: 0,
    total_hours: 0,
    bookshelves_built: 0,
    families_served: 0,
    events_completed: 0
  });
  const [corporateStats, setCorporateStats] = useState(null);
  const [corporateEvents, setCorporateEvents] = useState([]);
  const [impactReports, setImpactReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedMetrics, setEditedMetrics] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [metricsRes, corpStatsRes, eventsRes, reportsRes] = await Promise.all([
        fetch(`${API}/metrics`, { credentials: 'include' }),
        fetch(`${API}/corporate/stats`, { credentials: 'include' }),
        fetch(`${API}/corporate/events`, { credentials: 'include' }),
        fetch(`${API}/admin/impact-reports`, { credentials: 'include' })
      ]);

      if (metricsRes.ok) {
        const data = await metricsRes.json();
        setMetrics(data);
        setEditedMetrics(data);
      }
      if (corpStatsRes.ok) {
        const data = await corpStatsRes.json();
        setCorporateStats(data);
      }
      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setCorporateEvents(data);
      }
      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setImpactReports(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const saveMetrics = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/metrics`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editedMetrics)
      });

      if (response.ok) {
        setMetrics(editedMetrics);
        setEditMode(false);
        toast.success('Metrics updated successfully');
      } else {
        toast.error('Failed to update metrics');
      }
    } catch (error) {
      console.error('Error saving metrics:', error);
      toast.error('Failed to update metrics');
    } finally {
      setSaving(false);
    }
  };

  const generateImpactReport = async () => {
    if (!selectedEvent) return;
    
    setGeneratingReport(true);
    try {
      const response = await fetch(`${API}/admin/corporate/events/${selectedEvent.event_id}/generate-report`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Impact report generated and sent!');
        setShowReportDialog(false);
        setSelectedEvent(null);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const openReportDialog = (event) => {
    setSelectedEvent(event);
    setShowReportDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const impactCards = [
    { 
      key: 'total_volunteers',
      title: 'Total Volunteers', 
      value: metrics.total_volunteers, 
      icon: Users, 
      color: 'bg-[#1a3a5c]',
      iconBg: 'bg-white/20',
      textColor: 'text-white',
      editable: false
    },
    { 
      key: 'total_hours',
      title: 'Volunteer Hours', 
      value: metrics.total_hours, 
      icon: Clock, 
      color: 'bg-[#5cc8e8]',
      iconBg: 'bg-white/30',
      textColor: 'text-[#1a3a5c]',
      editable: false
    },
    { 
      key: 'bookshelves_built',
      title: 'Bookshelves Built', 
      value: metrics.bookshelves_built, 
      icon: BookOpen, 
      color: 'bg-[#F4D03F]',
      iconBg: 'bg-white/30',
      textColor: 'text-[#1a3a5c]',
      editable: true
    },
    { 
      key: 'families_served',
      title: 'Families Served', 
      value: metrics.families_served, 
      icon: Home, 
      color: 'bg-emerald-500',
      iconBg: 'bg-white/20',
      textColor: 'text-white',
      editable: true
    },
    { 
      key: 'events_completed',
      title: 'Events Completed', 
      value: metrics.events_completed, 
      icon: TrendingUp, 
      color: 'bg-purple-500',
      iconBg: 'bg-white/20',
      textColor: 'text-white',
      editable: true
    },
    { 
      key: 'active_volunteers',
      title: 'Active Volunteers', 
      value: metrics.active_volunteers, 
      icon: Users, 
      color: 'bg-sky-500',
      iconBg: 'bg-white/20',
      textColor: 'text-white',
      editable: false
    },
  ];

  const completedEvents = corporateEvents.filter(e => e.status === 'Completed');

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="impact-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Impact Dashboard
            </h1>
            <p className="text-slate-500 mt-1">Track and celebrate your organization's impact</p>
          </div>
          {!editMode ? (
            <Button 
              onClick={() => setEditMode(true)} 
              variant="outline"
              className="border-2"
              data-testid="edit-metrics-btn"
            >
              Edit Metrics
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setEditMode(false); setEditedMetrics(metrics); }}>
                Cancel
              </Button>
              <Button 
                onClick={saveMetrics} 
                disabled={saving}
                className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                data-testid="save-metrics-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="corporate" data-testid="corporate-tab">Corporate Impact</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Impact Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {impactCards.map((card) => (
                <Card key={card.key} className={`${card.color} border-0 shadow-lg overflow-hidden`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`text-sm ${card.textColor} opacity-80`}>{card.title}</p>
                        {editMode && card.editable ? (
                          <Input
                            type="number"
                            value={editedMetrics[card.key]}
                            onChange={(e) => setEditedMetrics(prev => ({ 
                              ...prev, 
                              [card.key]: parseInt(e.target.value) || 0 
                            }))}
                            className="mt-2 bg-white/90 text-[#1a3a5c] font-bold text-2xl h-12"
                            data-testid={`edit-${card.key}`}
                          />
                        ) : (
                          <p className={`text-4xl font-bold ${card.textColor} mt-2`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {card.value.toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className={`w-14 h-14 ${card.iconBg} rounded-xl flex items-center justify-center`}>
                        <card.icon className={`w-7 h-7 ${card.textColor}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Info Card */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1a3a5c]">About Impact Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-600">
                <p>
                  These metrics help track the overall impact of The Bookshelf Project. Some values are automatically 
                  calculated from volunteer data, while others can be manually updated.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-semibold text-[#1a3a5c] mb-2">Automatically Tracked</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Total Volunteers (from applications)</li>
                      <li>• Active Volunteers (status = Active)</li>
                      <li>• Volunteer Hours (from hour logs)</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-semibold text-[#1a3a5c] mb-2">Manually Updated</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Bookshelves Built</li>
                      <li>• Families Served</li>
                      <li>• Events Completed</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Corporate Impact Tab */}
          <TabsContent value="corporate" className="space-y-6">
            {/* Corporate Stats Summary */}
            {corporateStats && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-blue-100">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Corporate Partners</p>
                        <p className="text-3xl font-bold text-[#1a3a5c]">{corporateStats.total_partners || 0}</p>
                      </div>
                      <Building2 className="w-10 h-10 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md bg-gradient-to-br from-green-50 to-green-100">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Corporate Events</p>
                        <p className="text-3xl font-bold text-[#1a3a5c]">{corporateStats.total_events || 0}</p>
                      </div>
                      <Calendar className="w-10 h-10 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md bg-gradient-to-br from-purple-50 to-purple-100">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Corporate Volunteers</p>
                        <p className="text-3xl font-bold text-[#1a3a5c]">{corporateStats.total_volunteers || 0}</p>
                      </div>
                      <Users className="w-10 h-10 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-amber-100">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-600 font-medium">Sponsorship Total</p>
                        <p className="text-3xl font-bold text-[#1a3a5c]">
                          ${(corporateStats.total_sponsorship || 0).toLocaleString()}
                        </p>
                      </div>
                      <DollarSign className="w-10 h-10 text-amber-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Generate Impact Reports Section */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Generate Impact Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                {completedEvents.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Generate and send impact reports to corporate partners after their volunteer events.
                    </p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Company</TableHead>
                          <TableHead>Event</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Volunteers</TableHead>
                          <TableHead>Report Status</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {completedEvents.map((event) => (
                          <TableRow key={event.event_id}>
                            <TableCell className="font-medium">{event.company_name}</TableCell>
                            <TableCell>{event.title}</TableCell>
                            <TableCell>{formatDate(event.date)}</TableCell>
                            <TableCell>{event.actual_volunteers || event.registered_count || 0}</TableCell>
                            <TableCell>
                              {event.impact_report_sent ? (
                                <Badge className="bg-green-100 text-green-800">Sent</Badge>
                              ) : (
                                <Badge variant="outline" className="text-slate-500">Not Sent</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                onClick={() => openReportDialog(event)}
                                disabled={event.impact_report_sent}
                                className={event.impact_report_sent 
                                  ? "bg-slate-100 text-slate-400" 
                                  : "bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                                }
                                data-testid={`generate-report-${event.event_id}`}
                              >
                                {event.impact_report_sent ? 'Sent' : (
                                  <>
                                    <Send className="w-3 h-3 mr-1" />
                                    Generate
                                  </>
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No completed corporate events yet.</p>
                    <p className="text-sm">Impact reports can be generated after events are marked as completed.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Past Impact Reports */}
            {impactReports.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Generated Impact Reports
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Event Date</TableHead>
                        <TableHead>Generated</TableHead>
                        <TableHead>Volunteers</TableHead>
                        <TableHead>Hours</TableHead>
                        <TableHead>Bookshelves</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {impactReports.map((report) => (
                        <TableRow key={report.report_id}>
                          <TableCell className="font-medium">{report.company_name}</TableCell>
                          <TableCell>{formatDate(report.event_date)}</TableCell>
                          <TableCell>{formatDate(report.created_at)}</TableCell>
                          <TableCell>{report.volunteer_count}</TableCell>
                          <TableCell>{report.service_hours}</TableCell>
                          <TableCell>{report.bookshelves_built}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewingReport(report)}
                              data-testid={`view-report-${report.report_id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Generate Report Dialog */}
        <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1a3a5c]" />
                Generate Impact Report
              </DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4">
                <p className="text-slate-600">
                  Generate and send an impact report for <strong>{selectedEvent.company_name}</strong>'s 
                  volunteer event on {formatDate(selectedEvent.date)}.
                </p>
                
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <h4 className="font-semibold text-[#1a3a5c]">Report Preview</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-500">Volunteers:</span>
                      <span className="ml-2 font-medium">{selectedEvent.actual_volunteers || selectedEvent.registered_count || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Hours:</span>
                      <span className="ml-2 font-medium">{selectedEvent.impact?.service_hours || selectedEvent.duration_hours || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Bookshelves:</span>
                      <span className="ml-2 font-medium">{selectedEvent.impact?.bookshelves_built || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Families:</span>
                      <span className="ml-2 font-medium">{selectedEvent.impact?.families_served || 0}</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-slate-500">
                  The report will be emailed to: <strong>{selectedEvent.contact_email}</strong>
                </p>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowReportDialog(false)} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={generateImpactReport}
                    disabled={generatingReport}
                    className="flex-1 bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                    data-testid="confirm-generate-report"
                  >
                    {generatingReport ? 'Generating...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Generate & Send
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View Report Dialog */}
        <Dialog open={!!viewingReport} onOpenChange={() => setViewingReport(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#1a3a5c]" />
                Impact Report Details
              </DialogTitle>
            </DialogHeader>
            {viewingReport && (
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-br from-[#1a3a5c] to-[#2a4a6c] text-white rounded-lg">
                  <h3 className="text-lg font-semibold">{viewingReport.company_name}</h3>
                  <p className="text-white/80 text-sm">Event Date: {formatDate(viewingReport.event_date)}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                    <p className="text-2xl font-bold text-[#1a3a5c]">{viewingReport.volunteer_count}</p>
                    <p className="text-xs text-slate-500">Volunteers</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <Clock className="w-6 h-6 mx-auto mb-2 text-green-600" />
                    <p className="text-2xl font-bold text-[#1a3a5c]">{viewingReport.service_hours}</p>
                    <p className="text-xs text-slate-500">Service Hours</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <BookOpen className="w-6 h-6 mx-auto mb-2 text-amber-600" />
                    <p className="text-2xl font-bold text-[#1a3a5c]">{viewingReport.bookshelves_built}</p>
                    <p className="text-xs text-slate-500">Bookshelves</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <Home className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                    <p className="text-2xl font-bold text-[#1a3a5c]">{viewingReport.families_served}</p>
                    <p className="text-xs text-slate-500">Families Served</p>
                  </div>
                </div>
                
                <div className="text-sm text-slate-500">
                  <p>Report ID: {viewingReport.report_id}</p>
                  <p>Generated: {formatDate(viewingReport.created_at)}</p>
                  <p>Sent to: {viewingReport.sent_to}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
